import { pipe } from 'rambda';
import * as React from 'react';
import ChevronDownSmall from 'wix-ui-icons-common/ChevronDownSmall';
import {
  sortMembersBySortOrderFirst,
  sortNodesBySortOrderFirst,
  sortRelatedGuidesBySortOrderFirst,
} from '../../../../../utils/applyMenuConfigChanges/addSortOrderProperty/sortBySortOrder';
import { isMainEntity } from '../../../../../utils/flattenTree/utils/extractors';
import { hasGuides } from '../../../../../utils/treeVisitors/createGuideTags';
import { NODE_TYPES } from '../../../../../utils/typeUtils';
import { ApiDisplay, API_TYPES } from '../../../ApiDisplay';
import { isSubTreeKey } from '../../../NavigationMenu/utils';
import { IntersectionSubscriber } from '../../../SectionIntersectionDetection/IntersectionSubscriber';
import MemberDocs, { MemberTypes } from '../../../TypeDetails/MemberDocs';
import { AdditionalInfo } from '../../AdditionalInfo';
import { IndexPageJsView } from '../IndexPageJsView';
import { IndexPageRESTView } from '../IndexPageRESTView';
import { mdGuideToMemberFormat, nodeToMemberFormat } from '../utils';
import style from './ApiReferenceCategoryIndexPage.scss';
import { Button } from '../../../Layout/Button';
import { Text } from '../../../PageElements/Text';

export const ApiReferenceCategoryIndexPage = ({
  node,
  isOpened,
  onShowMoreClick,
  prefix,
  depth,
}) => {
  const {
    operations = [],
    properties = [],
    messages = [],
    relatedGuides = [],
    clientEvents = [],
  } = node.data;
  const JSLayoutType = node?.data?.layoutType === 'JS';
  const shouldHaveShowMoreButton = depth !== 0;

  const getCurrentNodeMdGuide = () => {
    const currentTreeGuides = node.data.guides || node.data.relatedGuides;
    return sortRelatedGuidesBySortOrderFirst(currentTreeGuides).map(
      mdGuideToMemberFormat,
    );
  };

  const getNodeSubTrees = () => {
    return sortNodesBySortOrderFirst(node)
      .filter(
        (nodeKey) =>
          isSubTreeKey(nodeKey) && node[nodeKey].type !== NODE_TYPES.TAG,
      )
      .map(pipe((key) => node[key], nodeToMemberFormat));
  };

  const getNodeSubTreesOfTypeTag = () => {
    return sortNodesBySortOrderFirst(node)
      .filter(
        (nodeKey) =>
          isSubTreeKey(nodeKey) && node[nodeKey].type === NODE_TYPES.TAG,
      )
      .map((key) => node[key]);
  };

  const subCategories = getNodeSubTrees();

  const getWebhooks = () => {
    const webhooks =
      node.data.events &&
      node.data.events.map((member) => ({
        ...member,
        name: member.displayName,
        docs: {
          ...(member.docs || {}),
          summary: member.description || member.docs?.summary,
        },
      }));

    return webhooks ? sortMembersBySortOrderFirst(webhooks) : [];
  };

  const getFirstGuideLink = () => {
    const sortedGuides =
      hasGuides(node) && sortRelatedGuidesBySortOrderFirst(relatedGuides);
    return isOpened && sortedGuides[0] ? sortedGuides[0].urlFormat : '';
  };

  const tags = getNodeSubTreesOfTypeTag();
  const mdGuides = getCurrentNodeMdGuide();
  const webhooks = getWebhooks();
  const mainEntity = messages.find(isMainEntity);

  return (
    <React.Fragment>
      <div className={style.indexPageNodeInfo}>
        <IntersectionSubscriber
          className={style.intersectionSubscriber}
          tagName="div"
          id={prefix}
        />
        <MemberDocs
          memberType={MemberTypes.API_REFERENCE_CATEGORY}
          memberObject={node.data}
          node={node}
          prefix={prefix}
          isMainCategory={depth === 0}
          firstGuideLink={getFirstGuideLink()}
        />
        {isOpened && (
          <div data-hook="index-page-tables">
            {JSLayoutType ? (
              <>
                <IndexPageJsView
                  operations={operations}
                  properties={properties}
                  clientEvents={clientEvents}
                  subCategories={subCategories}
                  mdGuides={mdGuides}
                  prefix={prefix}
                  tags={tags}
                />
                <AdditionalInfo data={node.data} />
              </>
            ) : (
              <>
                <ApiDisplay.JS>
                  <IndexPageJsView
                    operations={operations}
                    properties={properties}
                    clientEvents={clientEvents}
                    subCategories={subCategories}
                    mdGuides={mdGuides}
                    prefix={prefix}
                    tags={tags}
                  />
                  <AdditionalInfo data={node.data} />
                </ApiDisplay.JS>
                <ApiDisplay.REST
                  showOnTypes={[API_TYPES.RPC_REST, API_TYPES.RPC]}
                >
                  <IndexPageRESTView
                    mainEntity={mainEntity}
                    operations={operations}
                    properties={properties}
                    webhooks={webhooks}
                    mdGuides={mdGuides}
                    clientEvents={clientEvents}
                    subCategories={subCategories}
                    prefix={prefix}
                    tags={tags}
                    node={node}
                  />
                </ApiDisplay.REST>
              </>
            )}
          </div>
        )}
        {shouldHaveShowMoreButton && !isOpened && (
          <div className={style.buttonContainer}>
            <Button dataHook="show-more-button" onClick={onShowMoreClick}>
              <Text size="small" weight="normal" light>
                Show Details
              </Text>{' '}
              <ChevronDownSmall />
            </Button>
          </div>
        )}
      </div>
      <div className={style.line} />
    </React.Fragment>
  );
};
